import { useEffect, useState } from 'react'
import type { OmegaRow } from '../types'
import { fetchOmegaRaw } from '../lib/api'
import { ExportButtons } from '../components/ExportButtons'
import { money, prettyDate } from '../lib/format'
import { useBrandFilter } from '../lib/brand'

const STATUS_STYLES: Record<string, string> = {
  COMPLETED: 'bg-emerald-500/20 text-emerald-300',
  FAILED: 'bg-red-500/20 text-red-300',
  CANCELLED: 'bg-slate-500/20 text-slate-300',
  PENDING: 'bg-yellow-500/20 text-yellow-300',
}

export function Omega() {
  const [brand] = useBrandFilter()
  const [rows, setRows] = useState<OmegaRow[] | null>(null)
  const [limit, setLimit] = useState<number>(1000)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function load(n = limit) {
    setLoading(true)
    setError(null)
    try {
      setRows(await fetchOmegaRaw(n))
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand])

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Omega</h1>
          <p className="text-sm text-slate-400">
            Raw deposits pulled from Omega (`recon_omega_payments`). Most recent first.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-400">
            Rows:{' '}
            <select
              value={limit}
              onChange={(e) => {
                const v = Number(e.target.value)
                setLimit(v)
                load(v)
              }}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-200"
            >
              <option value={500}>500</option>
              <option value={1000}>1 000</option>
              <option value={5000}>5 000</option>
              <option value={20000}>20 000</option>
            </select>
          </label>
          <ExportButtons name="omega" rows={rows as unknown as Record<string, unknown>[] | null} />
        </div>
      </div>

      {error && (
        <div className="border border-red-500/40 bg-red-500/10 text-red-200 rounded px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {loading && !rows && <div className="text-slate-400 text-sm">Loading…</div>}

      {rows && (
        <div className="border border-slate-800 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wide">
              <tr className="whitespace-nowrap">
                {brand === 'ALL' && <th className="text-left px-3 py-2">Brand</th>}
                <th className="text-left px-3 py-2">Payment ID</th>
                <th className="text-left px-3 py-2">Party ID</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Method</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-right px-3 py-2">Processed</th>
                <th className="text-right px-3 py-2">Fee</th>
                <th className="text-right px-3 py-2">Gross</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">Request date (UTC)</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">Process date (UTC)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const processed = r.processed_amount ? parseFloat(r.processed_amount) : 0
                const fee = r.fee ? parseFloat(r.fee) : 0
                return (
                  <tr key={`${r.brand}-${r.payment_id}`} className="border-t border-slate-800/80">
                    {brand === 'ALL' && (
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${r.brand === 'ROLLA' ? 'bg-red-500/20 text-red-300' : 'bg-indigo-500/20 text-indigo-300'}`}>
                          {r.brand}
                        </span>
                      </td>
                    )}
                    <td className="px-3 py-2 font-mono">{r.payment_id}</td>
                    <td className="px-3 py-2 font-mono">{r.party_id ?? '—'}</td>
                    <td
                      className="px-3 py-2 text-slate-300 max-w-[220px] truncate"
                      title={r.email ?? ''}
                    >
                      {r.email ?? '—'}
                    </td>
                    <td className="px-3 py-2">{r.method}</td>
                    <td className="px-3 py-2">{r.transaction_type}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {money(r.processed_amount)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {money(r.fee)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {money((processed + fee).toFixed(2))}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs ${
                          STATUS_STYLES[r.status] ?? 'bg-slate-500/20 text-slate-300'
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                      {r.request_date ? prettyDate(r.request_date) : '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                      {r.process_date ? prettyDate(r.process_date) : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
